import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl, formatPrice, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { Mail, Phone, Trash2, Calendar, User, FileText, ExternalLink, Car as CarIcon } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_LABELS = { pending: "In attesa", confirmed: "Confermata", rejected: "Rifiutata" };
const STATUS_STYLES = {
  pending: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30",
};

export default function AdminReservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [receipts, setReceipts] = useState({}); // { path: blobUrl }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reservations");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const loadReceipt = async (path) => {
    if (receipts[path]) return receipts[path];
    try {
      const r = await api.get(`/images/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setReceipts((prev) => ({ ...prev, [path]: url }));
      return url;
    } catch { toast.error("Errore caricamento ricevuta"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}`, { status });
      toast.success("Stato aggiornato");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Prenotazione eliminata");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);

  return (
    <AdminShell title="Prenotazioni">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {["all", "pending", "confirmed", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm transition-colors ${
                filter === s ? "bg-gold text-midnight font-semibold" : "bg-midnight-light text-white/60 hover:text-white"
              }`}
              data-testid={`res-filter-${s}`}
            >
              {s === "all" ? "Tutte" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <p className="text-xs text-white/40 md:ml-auto">{filtered.length} prenotazioni</p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-midnight-light border border-white/5 p-16 text-center">
          <FileText size={36} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/60">Nessuna prenotazione in questa categoria</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="reservations-list">
          {filtered.map((r) => (
            <div key={r.id} className="bg-midnight-light border border-white/5 p-6" data-testid={`reservation-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/20 flex items-center justify-center shrink-0">
                    <User size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{r.name} {r.surname}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-white/60">
                      <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-gold" data-testid={`res-phone-${r.id}`}>
                        <Phone size={11} /> {r.phone}
                      </a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-gold" data-testid={`res-email-${r.id}`}>
                        <Mail size={11} /> {r.email}
                      </a>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {new Date(r.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                    <SelectTrigger className={`w-44 rounded-sm border ${STATUS_STYLES[r.status]}`} data-testid={`res-status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-destructive hover:bg-destructive hover:text-white rounded-sm transition-colors" data-testid={`delete-res-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questa prenotazione?</AlertDialogTitle>
                        <AlertDialogDescription>L'operazione è irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(r.id)} className="bg-destructive text-white">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-white/5 text-xs">
                <div className="flex items-start gap-3">
                  <CarIcon size={16} className="text-gold mt-0.5" />
                  <div>
                    <p className="text-white/40 uppercase tracking-widest mb-1">Auto</p>
                    <p className="font-medium">
                      {r.car_snapshot?.brand} {r.car_snapshot?.model} ({r.car_snapshot?.year})
                    </p>
                    {r.car_snapshot?.ref_code && (
                      <p className="text-gold font-mono text-[11px] tracking-wider mt-1" data-testid={`res-car-ref-${r.id}`}>
                        Rif. {r.car_snapshot.ref_code}
                      </p>
                    )}
                    {r.car_snapshot?.price && (
                      <p className="text-gold text-[11px] mt-1">{formatPrice(r.car_snapshot.price)}</p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Ricevuta bonifico</p>
                  {r.receipt_image_path ? (
                    <ReceiptViewer path={r.receipt_image_path} contentType={r.receipt_content_type} loader={loadReceipt} url={receipts[r.receipt_image_path]} />
                  ) : (
                    <p className="text-white/40 italic">Non caricata</p>
                  )}
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Note cliente</p>
                  <p className="text-white/80 whitespace-pre-line">{r.notes || "—"}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

function ReceiptViewer({ path, contentType, loader, url }) {
  const [open, setOpen] = useState(false);
  const handleOpen = async () => {
    const u = await loader(path);
    if (u) setOpen(true);
  };
  const isPdf = contentType === "application/pdf";
  return (
    <>
      <button
        onClick={handleOpen}
        className="text-gold hover:underline text-xs flex items-center gap-1"
        data-testid={`view-receipt-${path}`}
      >
        <ExternalLink size={12} /> Visualizza ricevuta
      </button>
      {open && url && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {isPdf ? (
              <iframe src={url} className="w-full h-[80vh] bg-white" title="Ricevuta" />
            ) : (
              <img src={url} alt="Ricevuta" className="w-full max-h-[90vh] object-contain" />
            )}
            <button
              onClick={() => setOpen(false)}
              className="mt-4 mx-auto block btn-gold px-6 py-2 text-xs uppercase tracking-widest font-semibold"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
